import { useScrollReveal } from '../../../hooks/useScrollReveal';

const caseStudies = [
  {
    industry: "Home Service Company",
    tag: "Home Services",
    metric: "30%",
    metricLabel: "More Booked Jobs",
    problem: "Missed inbound calls during peak hours—no follow-up, no system.",
    solution: "AI voice agent + SMS automation that captures and responds instantly.",
    timeline: "30 days",
    icon: "ri-hammer-line",
  },
  {
    industry: "Medspa Group",
    tag: "Medical & Medspa",
    metric: "40%",
    metricLabel: "Appointment Retention",
    problem: "Empty slots and a 40% no-show rate draining monthly revenue.",
    solution: "Smart scheduling with automated reminders and rebooking sequences.",
    timeline: "45 days",
    icon: "ri-hospital-line",
  },
  {
    industry: "Regional Law Firm",
    tag: "Legal",
    metric: "3×",
    metricLabel: "Faster Client Intake",
    problem: "Slow intake process filtering out high-value clients before consultation.",
    solution: "Automated qualification workflow that pre-screens and routes leads 24/7.",
    timeline: "21 days",
    icon: "ri-scales-3-line",
  },
];

const CaseStudySection = () => {
  const { ref: headRef, visible: headVisible } = useScrollReveal();
  const { ref: metricsRef, visible: metricsVisible } = useScrollReveal();

  return (
    <section className="bg-[#f5f5f7] dark:bg-[#0d0d0d] py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={headRef}
          className={`reveal-el ${headVisible ? 'is-visible' : ''} text-center max-w-3xl mx-auto mb-20`}
        >
          <p className="text-sapphire text-xs font-bold uppercase tracking-widest mb-8">Case Studies</p>
          <h2 className="text-5xl lg:text-6xl xl:text-7xl font-black text-[#1d1d1f] dark:text-white leading-[1.04] tracking-tight">
            Real Systems.<br />Real Results.
          </h2>
        </div>

        {/* Metric cards */}
        <div
          ref={metricsRef}
          className="border border-[#d2d2d7] dark:border-white/10 rounded-3xl overflow-hidden bg-white dark:bg-white/[0.03]"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#d2d2d7] dark:divide-white/10">
            {caseStudies.map((cs, i) => (
              <div
                key={cs.industry}
                className={`reveal-el stagger-${i + 1} ${metricsVisible ? 'is-visible' : ''} p-10 lg:p-14`}
              >
                {/* Giant metric */}
                <div className="text-8xl lg:text-9xl font-black text-[#1d1d1f] dark:text-white tracking-tight leading-none mb-3">
                  {cs.metric}
                </div>
                <p className="text-xs font-bold text-sapphire uppercase tracking-widest mb-8">{cs.metricLabel}</p>

                {/* Industry tag + icon */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-sapphire/10">
                    <i className={`${cs.icon} text-sapphire text-base`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#6e6e73] dark:text-white/40 uppercase tracking-wider">{cs.tag}</p>
                    <p className="text-sm font-bold text-[#1d1d1f] dark:text-white">{cs.industry}</p>
                  </div>
                </div>

                {/* Problem + Solution */}
                <div className="space-y-4">
                  <div className="bg-[#f5f5f7] dark:bg-white/[0.05] rounded-2xl p-5">
                    <p className="text-[10px] font-black text-[#6e6e73] dark:text-white/40 uppercase tracking-widest mb-2">Problem</p>
                    <p className="text-sm text-[#6e6e73] dark:text-white/40 leading-relaxed">{cs.problem}</p>
                  </div>
                  <div className="bg-sapphire/5 dark:bg-sapphire/10 border border-sapphire/10 rounded-2xl p-5">
                    <p className="text-[10px] font-black text-sapphire uppercase tracking-widest mb-2">Solution</p>
                    <p className="text-sm text-[#6e6e73] dark:text-white/40 leading-relaxed">{cs.solution}</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex items-center gap-2 mt-6">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-timer-flash-line text-sapphire text-sm"></i>
                  </span>
                  <span className="text-xs font-semibold text-[#6e6e73] dark:text-white/40">Live in {cs.timeline}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudySection;
